import Table from "../../../components/RecordTable";
import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
  Switch,
  Row,
  Col,
  Select,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  LockOutlined,
  UnlockOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { labAPI, userAPI } from "../../../services/api";

const { Title, Text } = Typography;
const { TextArea } = Input;

const Labs = () => {
  const navigate = useNavigate();
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [form] = Form.useForm();
  const [workspaceLab, setWorkspaceLab] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState();
  const [opening, setOpening] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchLabs = async () => {
    setLoading(true);
    try {
      const response = await labAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setLabs(response.data.labs || []);
      setPagination({
        ...pagination,
        total: response.data.pagination?.total || 0,
      });
    } catch (error) {
      message.error("Failed to fetch labs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current]);

  const handleSubmit = async (values) => {
    try {
      if (editingLab) {
        await labAPI.update(editingLab._id, values);
        message.success("Lab updated successfully");
      } else {
        await labAPI.create(values);
        message.success("Lab created successfully");
      }
      setModalVisible(false);
      fetchLabs();
    } catch (error) {
      message.error(error.message || "Operation failed");
    }
  };

  const handleSuspend = async (lab) => {
    try {
      await labAPI.update(lab._id, { isActive: !lab.isActive });
      message.success(lab.isActive ? "Lab suspended" : "Lab activated");
      fetchLabs();
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await labAPI.delete(id);
      message.success("Lab deleted successfully");
      fetchLabs();
    } catch (error) {
      message.error("Delete failed");
    }
  };

  const handleEdit = (lab) => {
    setEditingLab(lab);
    form.setFieldsValue(lab);
    setModalVisible(true);
  };

  const handleViewDashboard = async (lab) => {
    setOpening(true);
    try {
      let users = [], page = 1, pages = 1;
      do {
        const response = await userAPI.getAll({ role: 'labadmin', limit: 100, page });
        users.push(...(response.data?.users || []));
        pages = response.data?.pagination?.pages || 1;
        page++;
      } while (page <= pages);
      const owners = users.filter(user => String(user.labId?._id || user.labId) === String(lab._id));
      if (owners.length === 1) { navigate(`/lab/${owners[0]._id}`); return; }
      setAdmins(owners.length ? owners : users.filter(user => !user.labId && user.isActive));
      setSelectedAdmin(undefined);
      setWorkspaceLab(lab);
    } catch { message.error('Unable to load laboratory administrators. Please retry.'); }
    finally { setOpening(false); }
  };
  const openWorkspace = async () => {
    setOpening(true);
    try {
      const admin = admins.find(user => user._id === selectedAdmin);
      if (!admin) return;
      if (!admin.labId) await userAPI.update(admin._id, { labId: workspaceLab._id });
      navigate(`/lab/${admin._id}`);
    } catch (error) { message.error(error.message || 'Unable to link the administrator.'); }
    finally { setOpening(false); }
  };

  const columns = [
    {
      title: "Lab",
      key: "lab",
      render: (_, record) => (
        <Space>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid #e8e8e8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
            }}
          >
            {record.logo ? (
              <img
                src={record.logo}
                alt={record.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <ExperimentOutlined style={{ fontSize: 24, color: "#999" }} />
            )}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{record.name}</div>
            <div style={{ fontSize: 12, color: "#666" }}>{record.email}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Locations",
      dataIndex: "locations",
      key: "locations",
      render: (locations) => (
        <div>
          {locations?.length > 0 ? (
            <>
              <div>{locations[0].city}</div>
              {locations.length > 1 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  +{locations.length - 1} more
                </Text>
              )}
            </>
          ) : (
            <Text type="secondary">No locations</Text>
          )}
        </div>
      ),
    },
    {
      title: "Accreditation",
      dataIndex: "accreditation",
      key: "accreditation",
      render: (acc) =>
        acc ? (
          <Tag color="green">{acc}</Tag>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      ellipsis: true,
      render: (site) =>
        site ? (
          <a href={site} target="_blank" rel="noopener noreferrer">
            {site}
          </a>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "success" : "error"}>
          {isActive ? "Active" : "Suspended"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",


      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            loading={opening}
            onClick={() => handleViewDashboard(record)}
          >
            View Dashboard
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={record.isActive ? <LockOutlined /> : <UnlockOutlined />}
            onClick={() => handleSuspend(record)}

          >
            {record.isActive ? "Suspend" : "Activate"}
          </Button>
          <Popconfirm
            title="Delete Lab?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Lab Management
          </Title>
          <Text type="secondary">
            Manage labs, locations, and access their dashboards
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            form.resetFields();
            setEditingLab(null);
            setModalVisible(true);
          }}
        >
          Add New Lab
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={labs}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={(newPagination) => setPagination(newPagination)}
          // scroll={{ x: 1300 }}
          scroll={{ x: "max-content" }}
          components={{
            body: {
              wrapper: (props) => (
                <tbody {...props} style={{ transition: "none" }} />
              ),
            },
          }}
        />
      </Card>

      <Modal title={`Open ${workspaceLab?.name || 'laboratory'} workspace`} open={Boolean(workspaceLab)} onCancel={() => setWorkspaceLab(null)} onOk={openWorkspace} okText="Open workspace" confirmLoading={opening} okButtonProps={{ disabled: !selectedAdmin }}>
        <Alert type="info" showIcon message="Choose the administrator who manages this laboratory." description="The workspace stores tests and bookings under a lab administrator. Linking an existing account lets you open it directly here on future visits." />
        <Select aria-label="Lab administrator" placeholder="Select an administrator" style={{ width: '100%', marginTop: 20 }} value={selectedAdmin} onChange={setSelectedAdmin} options={admins.map(user => ({ value: user._id, label: `${user.name} (${user.email})` }))} />
        {!admins.length && <p>No unlinked administrator is available. <Button type="link" onClick={() => navigate('/superadmin/users/create', { state: { labId: workspaceLab._id, labName: workspaceLab.name } })}>Create a lab administrator</Button></p>}
      </Modal>
      <Modal
        title={editingLab ? "Edit Lab" : "Add New Lab"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Lab Name" rules={[{ required: true }]}>
            <Input size="large" placeholder="Dr. Lal PathLabs" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input size="large" placeholder="contact@lab.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true }]}
              >
                <Input size="large" placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Brief description about the lab" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="website" label="Website">
                <Input size="large" placeholder="https://lab.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="accreditation" label="Accreditation">
                <Input size="large" placeholder="NABL, CAP" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Suspended" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button size="large" onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" size="large" htmlType="submit">
                {editingLab ? "Update Lab" : "Create Lab"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Labs;
